<script>
  import { createEventDispatcher } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { sanitizeInputValue } from '@client/utils/sanitize';

  const dispatch = createEventDispatcher();

  export let fields = [];
  export let isSalesforce = false;
  export let activeFields = [];
  export let activeTextFields = [];
  export let activeReplyFields = [];
  export let activeReplyTextFields = [];

  let hovering = false;
  let startIndex = null;
  let fieldError = false;

  const updateFieldsList = (list, start, target) => {
    if (start < target) {
      list.splice(target + 1, 0, list[start]);
      list.splice(start, 1);
    } else {
      list.splice(target, 0, list[start]);
      list.splice(start + 1, 1);
    }

    return list;
  };

  const drop = (event, target) => {
    event.dataTransfer.dropEffect = 'move';

    fields = updateFieldsList(fields, startIndex, target);

    hovering = null;
    startIndex = null;

    fields = [...fields];
    dispatch('change', { fields });
  };

  const dragstart = (event, index) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.dropEffect = 'move';
    startIndex = index;
  };

  const onFieldRemove = (index) => {
    fields.splice(index, 1);
    fields = [...fields];
    dispatch('change', { fields });
  };

  const onAddField = () => {
    fields = [...fields, { name: '', hide: false, transform: false }];
    dispatch('change', { fields });
  };

  const onFieldKeyUp = ({ target }, index) => {
    const newValue = sanitizeInputValue(target.value);
    const previousValue = fields[index].name;
    const existingValues = fields.map(({ name }) => name);
    const hasError = existingValues.includes(newValue) && newValue !== previousValue;

    if (hasError) {
      fields[index].error = fieldError = hasError;
    } else {
      delete fields[index].error;
    }

    fields[index].name = newValue;

    fields = [...fields];
  };

  const onFieldUpdate = ({ target }, index, property) => {
    const newValue = property === 'name' ? sanitizeInputValue(target.value) : target.checked;

    if (fields[index].error) {
      return false;
    }

    fields[index][property] = newValue;

    fields = [...fields];
    dispatch('change', { fields });
  };
</script>

<div class="signature-fields" class:salesforce={isSalesforce}>
  {#if fields.length}
    <div class="field headers">
      <div class="field-details">
        <span />
        <span>{$format('label.HIDE')}</span>
        {#if isSalesforce}
          <span>{$format('label.TRANSFORM')}</span>
        {/if}
        <span />
      </div>
    </div>
  {/if}

  {#each fields as field, index}
    <div
      on:dragstart={(event) => dragstart(event, index)}
      on:drop|preventDefault={(event) => drop(event, index)}
      on:dragover|preventDefault
      on:dragenter|preventDefault={() => (hovering = index)}
      role="button"
      tabindex="0"
      class="field"
      class:dragging={hovering === index}
      draggable={true}
    >
      <div class="field-details">
        <Icon name="grip-vertical" class="cursor-move" />
        <input
          type="checkbox"
          name="{field}-hide"
          bind:checked={field.hide}
          on:change={(e) => onFieldUpdate(e, index, 'hide')}
        />

        {#if isSalesforce}
          <input
            type="checkbox"
            name="{field}-transform"
            bind:checked={field.transform}
            on:change={(e) => onFieldUpdate(e, index, 'transform')}
          />
        {/if}

        <input
          type="text"
          data-testid="field-name"
          class:error={field.error}
          name="{field}-name"
          value={field.name}
          on:change={(e) => onFieldUpdate(e, index, 'name')}
          on:keyup={(e) => onFieldKeyUp(e, index)}
        />
        {#if activeFields.includes(field.name) || activeTextFields.includes(field.name) || activeReplyFields.includes(field.name) || activeReplyTextFields.includes(field.name)}
          <div class="active" id="{field.name}-fields-tooltip">&nbsp;</div>
          <Tooltip target="{field.name}-fields-tooltip">
            {$format('label.TOOLTIP.FIELD_BEING_USED_IN')}
            <ul class="active-fields-list">
              {#if activeFields.includes(field.name)}
                <li>{$format('label.SIGNATURE')}</li>
              {/if}
              {#if activeTextFields.includes(field.name)}
                <li>{$format('label.TEXT_SIGNATURE')}</li>
              {/if}
              {#if activeReplyFields.includes(field.name)}
                <li>{$format('label.REPLY_SIGNATURE')}</li>
              {/if}
              {#if activeReplyTextFields.includes(field.name)}
                <li>{$format('label.REPLY_TEXT_SIGNATURE')}</li>
              {/if}
            </ul>
          </Tooltip>
        {/if}
        <Icon
          name="x"
          class="pointer"
          data-testid="remove-field"
          on:click={() => onFieldRemove(index)}
          role="button"
          tabindex="0"
        />
      </div>
      {#if field.error}
        <div class="field-error">{$format('label.FIELD_NAME_ALREADY_EXISTS')}</div>
      {/if}
    </div>
  {/each}
</div>

<Button color="secondary" data-testid="add-field" size="sm" on:click={onAddField} bind:disabled={fieldError}>
  {$format('label.ADD_FIELD')}
</Button>

<style lang="scss">
  .signature-fields {
    display: flex;
    flex-direction: column;
    margin-bottom: var(--margin-large);

    .field {
      background: var(--color-white);
      border: 1px solid transparent;
      border-bottom: 1px solid var(--border-color);
      position: relative;
      transition: margin-left 0.25s ease-in-out;

      .field-details {
        align-items: center;
        display: grid;
        grid-template-columns: 28px 28px auto 20px;
        padding: var(--padding-small) 0;

        :global(i) {
          width: min-content;
        }
      }

      .field-error {
        color: var(--color-red-400);
        padding: var(--padding-small) 0 var(--padding-small) 84px;
      }

      &:last-child {
        border-bottom: 0;
      }

      input[type='text'] {
        border: 0;
        padding: var(--padding-base) 0;

        &:focus {
          box-shadow: inset 0 -1px 0 0 var(--color-blue-400);
          outline: none;
        }

        &.error,
        &.error:focus {
          background-color: var(--color-red-0);
          box-shadow: inset 0 -1px 0 0 var(--color-red-400);
        }
      }

      input[type='checkbox'] {
        display: block;
        width: 16px;
      }

      .active {
        background-color: var(--color-gray-0);
        font-size: var(--font-size-small);
        position: absolute;
        margin-top: 3px;
        right: 28px;
        top: 13px;
        width: 10px;

        &:before {
          background-color: var(--color-green-400);
          border-radius: 100%;
          content: '';
          display: block;
          height: 10px;
          position: absolute;
          width: 10px;
        }
      }

      &.dragging {
        background-color: var(--color-gray-0);
        border-style: dashed;
        margin-left: 4px;

        input {
          background-color: var(--color-gray-0);
        }
      }
    }

    .field.headers {
      border-bottom: 0;
      font-size: var(--font-size-small);
    }

    &.salesforce {
      .field-details {
        grid-template-columns: 28px 28px 28px auto 20px;
      }
    }
  }

  :global(ul.active-fields-list) {
    list-style: disc;
    margin-left: var(--margin-large);
  }
</style>
